import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, TextField, Button, Paper,
    IconButton, Chip, Autocomplete, CircularProgress, Alert,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    EventNote as EventIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
    People as PeopleIcon,
    ArrowBack as BackIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { events, users } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.08) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const FadeUp = ({ children, delay = 0 }) => {
    const [ref, inView] = useInView();
    return (
        <Box ref={ref} sx={{
            opacity:   inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(32px)',
            transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
            willChange: 'opacity, transform',
        }}>
            {children}
        </Box>
    );
};

// ── Constants ─────────────────────────────────────────────────────────────────
const DURATIONS = [
    { label: '30 min', hours: 0.5 },
    { label: '1 hr',   hours: 1   },
    { label: '2 hr',   hours: 2   },
    { label: '3 hr',   hours: 3   },
    { label: 'All day', hours: 24 },
];

const toLocalISO = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const buildDateTime = (date, time) => {
    if (!date || !time) return null;
    const d = new Date(date);
    const t = new Date(time);
    d.setHours(t.getHours(), t.getMinutes(), 0, 0);
    return d;
};

const computeEnd = (start, hours) => {
    if (!start) return null;
    const end = new Date(start);
    if (hours === 24) { end.setHours(23, 59, 0, 0); }
    else { end.setTime(end.getTime() + hours * 3600 * 1000); }
    return end;
};

// ── SectionCard ───────────────────────────────────────────────────────────────
const SectionCard = ({ icon, label, children, delay = 0 }) => (
    <FadeUp delay={delay}>
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden', borderColor: 'rgba(0,0,0,0.1)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {React.cloneElement(icon, { sx: { fontSize: 17, color: '#7C3AED' } })}
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#374151' }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ px: 3, py: 3, bgcolor: 'white' }}>
                {children}
            </Box>
        </Paper>
    </FadeUp>
);

// ── Component ─────────────────────────────────────────────────────────────────
const CreateEvent = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const scrollY  = useScrollY();

    const [title,          setTitle]       = useState('');
    const [description,    setDescription] = useState('');
    const [location,       setLocation]    = useState('');
    const [durationHours,  setDuration]    = useState(2);
    const [timeSlots,      setTimeSlots]   = useState([{ date: null, startTime: null }]);
    const [selectedPeople, setPeople]      = useState([]);
    const [userOptions,    setUserOptions] = useState([]);
    const [searching,      setSearching]   = useState(false);
    const [error,          setError]       = useState('');
    const [submitting,     setSubmitting]  = useState(false);

    const handleUserSearch = useCallback((q) => {
        if (!q || q.trim().length < 3) { setUserOptions([]); return; }
        setSearching(true);
        users.search(q.trim())
            .then(res => setUserOptions((res.data || []).filter(u => u.id !== user.id)))
            .catch(() => setUserOptions([]))
            .finally(() => setSearching(false));
    }, [user.id]);

    const updateSlot = (i, field, val) =>
        setTimeSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
    const addSlot    = () => setTimeSlots(prev => [...prev, { date: null, startTime: null }]);
    const removeSlot = (i) => setTimeSlots(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const valid = timeSlots.filter(s => s.date && s.startTime);
        if (!title.trim())      { setError('Event title is required'); return; }
        if (valid.length === 0) { setError('Add at least one time option with a date and start time'); return; }
        setSubmitting(true);
        try {
            const proposed = valid.map(s => {
                const start = buildDateTime(s.date, s.startTime);
                return {
                    proposedStartTime: toLocalISO(start),
                    proposedEndTime:   toLocalISO(computeEnd(start, durationHours)),
                };
            });
            const response = await events.create(
                {
                    title:          title.trim(),
                    description:    description.trim(),
                    location:       location.trim(),
                    startTime:      proposed[0].proposedStartTime,
                    endTime:        proposed[0].proposedEndTime,
                    votingDeadline: null,
                    status:         'SCHEDULED',
                },
                proposed,
                user.id
            );
            const eventId = response.data.id;
            await Promise.all(selectedPeople.map(p => events.addParticipant(eventId, p.id)));
            navigate(`/events/${eventId}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create event. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box sx={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', py: 6, position: 'relative', overflow: 'clip' }}>
                <Box sx={{ position: 'absolute', top: -50, left: '-3%', width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', transform: `translateY(${scrollY*0.3}px)`, willChange: 'transform', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', top: -10, right: '4%',  width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', transform: `translateY(${scrollY*0.5}px)`, willChange: 'transform', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: '50%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', transform: `translateY(${scrollY*0.65}px)`, willChange: 'transform', pointerEvents: 'none' }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, transform: `translateY(${scrollY*0.2}px)`, willChange: 'transform' }}>
                    <Button onClick={() => navigate('/dashboard')} startIcon={<BackIcon />}
                        sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, mb: 2, px: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}>
                        Dashboard
                    </Button>
                    <Typography variant="h4" fontWeight={800} sx={{ color: 'white', mb: 0.5 }}>
                        Create Event
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Set up your event and invite people to vote on the best time
                    </Typography>
                </Container>
            </Box>

            {/* ── Form ─────────────────────────────────────────────────────── */}
            <Container maxWidth="lg" sx={{ pt: 5, pb: 10 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                    {/* Details */}
                    <SectionCard icon={<EventIcon />} label="Event Details" delay={0.05}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                    Title *
                                </Typography>
                                <TextField fullWidth variant="outlined" required size="small"
                                    placeholder="Give your event a name"
                                    value={title} onChange={e => setTitle(e.target.value)}
                                    inputProps={{ style: { fontSize: 18, fontWeight: 700 } }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                        Description
                                    </Typography>
                                    <TextField fullWidth variant="outlined" multiline rows={3} size="small"
                                        placeholder="What's this event about? (optional)"
                                        value={description} onChange={e => setDescription(e.target.value)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                        Location
                                    </Typography>
                                    <TextField fullWidth variant="outlined" size="small"
                                        placeholder="Where is it? (optional)"
                                        value={location} onChange={e => setLocation(e.target.value)}
                                        InputProps={{ startAdornment: <LocationIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} /> }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* Duration */}
                    <SectionCard icon={<TimeIcon />} label="Duration" delay={0.1}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {DURATIONS.map(d => (
                                <Box key={d.hours} onClick={() => setDuration(d.hours)} sx={{
                                    px: 2.5, py: 1, borderRadius: 99, cursor: 'pointer',
                                    border: '1.5px solid',
                                    borderColor: durationHours === d.hours ? '#7C3AED' : '#E5E7EB',
                                    bgcolor:     durationHours === d.hours ? '#7C3AED' : 'white',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#7C3AED', bgcolor: durationHours === d.hours ? '#6D28D9' : '#EDE9FE' },
                                }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: durationHours === d.hours ? 'white' : 'text.primary' }}>
                                        {d.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* Time Options */}
                    <SectionCard icon={<CalendarIcon />} label="Time Options" delay={0.15}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {timeSlots.map((slot, i) => {
                                const start = buildDateTime(slot.date, slot.startTime);
                                const end   = start ? computeEnd(start, durationHours) : null;
                                return (
                                    <Paper key={i} elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', borderColor: 'rgba(0,0,0,0.1)' }}>
                                        {/* Slot header */}
                                        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#FAFAFA', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" fontWeight={700} sx={{ color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                                Option {i + 1}
                                                {start && end && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 1.5, color: 'text.secondary', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                                                        — {format(start, 'EEE, MMM d')}
                                                        {durationHours !== 24 && ` · ${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`}
                                                        {durationHours === 24 && ' · All day'}
                                                    </Typography>
                                                )}
                                            </Typography>
                                            {timeSlots.length > 1 && (
                                                <IconButton size="small" onClick={() => removeSlot(i)}
                                                    sx={{ p: 0.5, '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' } }}>
                                                    <DeleteIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                        {/* Pickers */}
                                        <Box sx={{ px: 2.5, py: 2.5, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                            <DatePicker
                                                label="Date"
                                                value={slot.date}
                                                onChange={val => updateSlot(i, 'date', val)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <TimePicker
                                                label="Start time"
                                                value={slot.startTime}
                                                onChange={val => updateSlot(i, 'startTime', val)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                        </Box>
                                    </Paper>
                                );
                            })}

                            <Button startIcon={<AddIcon />} onClick={addSlot} size="small"
                                sx={{ alignSelf: 'flex-start', fontWeight: 700, color: '#7C3AED', borderRadius: 99, px: 2, py: 0.8, border: '1.5px dashed #A78BFA', '&:hover': { bgcolor: '#EDE9FE', borderColor: '#7C3AED' } }}>
                                Add another time option
                            </Button>
                        </Box>
                    </SectionCard>

                    {/* Invite People */}
                    <SectionCard icon={<PeopleIcon />} label="Invite People" delay={0.2}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Search for people to invite. You can also share the voting link after creating the event.
                        </Typography>
                        <Autocomplete
                            multiple
                            filterOptions={x => x}
                            options={userOptions}
                            getOptionLabel={o => o.firstName && o.lastName ? `${o.firstName} ${o.lastName} (${o.username})` : o.username}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={selectedPeople}
                            onChange={(_, val) => setPeople(val)}
                            onInputChange={(_, q) => handleUserSearch(q)}
                            loading={searching}
                            renderTags={(value, getTagProps) =>
                                value.map((o, idx) => (
                                    <Chip key={o.id} size="small"
                                        label={o.firstName && o.lastName ? `${o.firstName} ${o.lastName}` : o.username}
                                        {...getTagProps({ index: idx })}
                                        sx={{ fontWeight: 600, bgcolor: '#EDE9FE', color: '#6D28D9', border: '1px solid #C4B5FD' }}
                                    />
                                ))
                            }
                            renderInput={params => (
                                <TextField {...params} size="small"
                                    placeholder="Search by name (3+ characters)"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {searching ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </SectionCard>

                    {/* Error + Submit */}
                    {error && (
                        <FadeUp>
                            <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
                        </FadeUp>
                    )}

                    <FadeUp delay={0.22}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button type="submit" variant="contained" size="large" disabled={submitting}
                                sx={{ fontWeight: 700, borderRadius: 2.5, px: 5, py: 1.5, fontSize: '1rem', background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', boxShadow: '0 4px 14px rgba(124,58,237,0.3)', '&:hover': { boxShadow: '0 6px 20px rgba(124,58,237,0.45)' } }}>
                                {submitting ? 'Creating…' : 'Create Event'}
                            </Button>
                            <Button variant="outlined" size="large" onClick={() => navigate('/dashboard')}
                                sx={{ fontWeight: 700, borderRadius: 2.5, borderColor: 'rgba(0,0,0,0.2)', color: 'text.secondary', '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}>
                                Cancel
                            </Button>
                        </Box>
                    </FadeUp>

                </Box>
            </Container>
        </Box>
    );
};

export default CreateEvent;
